import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import { dispatch } from "../../../../state/store";
import { setNotificationCampaignToEdit } from "../../../../state/ducks";

import { alignItems, colors, strCode } from "../../../../consts";
import { t } from "../../../../services";
import { objectUtil } from "../../../../utils";
import { formatUtils } from "./utils/formatUtils";

import {
  CustomModalContainer,
  DiscardModal,
  FlexContainers,
  Modal,
  SmallButton,
  Stepper,
  TextButton,
  WrapperContainer
} from "../../../../components";
import { SplittedModalContentTemplate } from "../../../../templates";

import OverviewSection from "./sections/OverviewSection/OverviewSection";
import AudienceSection from "./sections/AudienceSection/AudienceSection";
import OverviewSectionSummary from "./components/OverviewSectionSummary/OverviewSectionSummary";
import AudienceSectionSummary from "./components/AudienceSectionSummary/AudienceSectionSummary";
import { SectionControlButtons } from "../../components/SectionControlButtons/SectionControlButtons";
import { DeactivateCampaignModal } from "../../components/DeactivateCampaignModal/DeactivateCampaignModal";

const NewNotificationCampaignModal = ({
  notificationCampaignData,
  active,
  setActive,
  loading,
  error,
  onSave,
  onUpdate,
  onDeactivate
}) => {
  const [discardModalActive, setDiscardModalActive] = useState(false);
  const [deactivateModalActive, setDeactivateModalActive] = useState(false);

  const [activeStep, setActiveStep] = useState(1);

  const [overviewData, setOverviewData] = useState(null);
  const [audienceData, setAudienceData] = useState(null);

  const [isSubmitted, setIsSubmitted] = useState(false);

  const [originalData, setOriginalData] = useState();

  const isEdit = useMemo(() => {
    return !!originalData;
  }, [originalData]);

  const formSectionsRef = useRef({
    saveOverviewSectionData: () => {},
    saveAudienceSectionData: () => {},
    resetOverviewSection: () => {}
  });

  const stepConfigs = [
    {
      step: 1,
      title: strCode.OVERVIEW,
      Component: (
        <OverviewSection ref={formSectionsRef} overviewData={overviewData} isEdit={isEdit} />
      ),
      DividerContent: <OverviewSectionSummary overviewSectionData={overviewData} />,
      getData: (validate) => formSectionsRef?.current?.saveOverviewSectionData(validate),
      setData: (data) => setOverviewData(data)
    },
    {
      step: 2,
      title: strCode.AUDIENCE_TITLE,
      Component: <AudienceSection ref={formSectionsRef} audienceData={audienceData} />,
      DividerContent: (
        <AudienceSectionSummary
          audienceData={audienceData}
          selectedCategory={overviewData?.selectedCategory?.id}
        />
      ),
      getData: (validate) => formSectionsRef?.current?.saveAudienceSectionData(validate),
      setData: (data) => setAudienceData(data),
      disabled: !overviewData
    }
  ];

  const activeStepConfig = useMemo(() => {
    return stepConfigs.find((config) => config.step === activeStep);
  }, [activeStep, stepConfigs]);

  const onChangeActiveStep = useCallback(
    (incrementalValue, step) => {
      if (activeStepConfig?.getData) {
        // Validate only when navigating to the next section
        const validate = incrementalValue !== -1 || !!step;

        const data = activeStepConfig.getData(validate);

        if (data) {
          activeStepConfig.setData(data);

          if (step) {
            setActiveStep(step);
          } else {
            setActiveStep((prev) => prev + incrementalValue);
          }

          return data;
        }
      }
    },
    [activeStepConfig]
  );

  useEffect(() => {
    if (notificationCampaignData) {
      const parsedData = formatUtils.parseNotificationCampaignData(notificationCampaignData);

      setOriginalData(parsedData);

      setOverviewData(parsedData.overviewData);
      setAudienceData(parsedData.audienceData);
    } else {
      setOriginalData(null);

      setOverviewData(null);
      setAudienceData(null);
    }
  }, [notificationCampaignData]);

  useEffect(() => {
    if (isSubmitted && !loading && !error && active) {
      onDiscard();
    }
  }, [isSubmitted, loading, error, active]);

  const onDiscard = () => {
    setOverviewData(null);
    setAudienceData(null);

    if (formSectionsRef?.current?.resetOverviewSection) {
      formSectionsRef?.current?.resetOverviewSection();
    }

    setActiveStep(1);
    setActive(false);
    setIsSubmitted(false);
    setDiscardModalActive(false);
    setOriginalData(null);

    dispatch(setNotificationCampaignToEdit(null));
  };

  const onFinish = () => {
    const data = onChangeActiveStep(0);

    if (data) {
      const notificationCampaignData = {
        overviewData,
        audienceData: data
      };

      const formattedNotificationCampaignData = formatUtils.toApiRequest(notificationCampaignData);

      onSave(formattedNotificationCampaignData);
      setIsSubmitted(true);
    }
  };

  const onSaveChanges = () => {
    const data = onChangeActiveStep(0);

    if (data) {
      const notificationCampaignUpdatedData = {
        overviewData,
        audienceData
      };

      switch (activeStep) {
        case 1:
          notificationCampaignUpdatedData.overviewData = data;
          break;
        case 2:
          notificationCampaignUpdatedData.audienceData = data;
          break;
      }

      const formattedNotificationCampaignData = formatUtils.toApiRequest(
        notificationCampaignUpdatedData
      );
      const formattedOriginalNotificationCampaignData = formatUtils.toApiRequest(originalData);

      const isSameData = objectUtil.objEquals(
        formattedNotificationCampaignData,
        formattedOriginalNotificationCampaignData
      );

      if (!isSameData) {
        onUpdate(formattedNotificationCampaignData);
        setIsSubmitted(true);
      } else {
        onDiscard();
      }
    }
  };

  const onDeactivateCampaign = () => {
    onDeactivate(notificationCampaignData.campaignName);
    setDeactivateModalActive(false);
    setIsSubmitted(true);
  };

  const onCloseModal = () => {
    if (!loading) {
      const notificationCampaignUpdatedData = {
        overviewData,
        audienceData
      };

      const data = activeStepConfig.getData(false);
      switch (activeStep) {
        case 1:
          notificationCampaignUpdatedData.overviewData = data;
          break;
        case 2:
          notificationCampaignUpdatedData.audienceData = data;
          break;
      }

      let isSameData;

      if (isEdit) {
        const formattedNotificationCampaignData = formatUtils.toApiRequest(
          notificationCampaignUpdatedData
        );
        const formattedOriginalNotificationCampaignData = formatUtils.toApiRequest(originalData);

        isSameData = objectUtil.objEquals(
          formattedNotificationCampaignData,
          formattedOriginalNotificationCampaignData
        );
      } else {
        isSameData =
          !notificationCampaignUpdatedData ||
          objectUtil.isNullOrDefault(notificationCampaignUpdatedData);
      }

      isSameData ? onDiscard() : setDiscardModalActive(true);
    }
  };

  return (
    <>
      <Modal
        id="new-notification-campaign-modal"
        active={active}
        setActive={onCloseModal}
        Container={CustomModalContainer}>
        <SplittedModalContentTemplate
          title={
            isEdit && notificationCampaignData
              ? notificationCampaignData?.campaignName
              : t(strCode.NOTIFICATION_CAMPAIGN_TITLE)
          }
          LeftSectionContent={
            <WrapperContainer
              Container={FlexContainers.FlexColumnBetween}
              alignItems={alignItems.start}
              width={"100%"}
              height={"100%"}>
              <Stepper
                stepConfigs={stepConfigs}
                activeStep={activeStep}
                setActiveStep={(step) => {
                  if (step !== activeStep) {
                    onChangeActiveStep(null, step);
                  }
                }}
              />

              {isEdit && (
                <WrapperContainer pb={8}>
                  <TextButton
                    color={colors.ERROR}
                    onClick={() => setDeactivateModalActive(true)}
                    disabled={loading}>
                    {t(strCode.DEACTIVATE_BTN)}
                  </TextButton>
                </WrapperContainer>
              )}
            </WrapperContainer>
          }
          BottomSectionContent={
            isEdit ? (
              <WrapperContainer
                Container={FlexContainers.FlexRowEnd}
                gap={8}
                width={"100%"}
                height={"100%"}>
                <SmallButton secondary disabled={loading} onClick={onCloseModal}>
                  {t(strCode.CANCEL_BTN)}
                </SmallButton>

                <SmallButton loading={loading} disabled={loading} onClick={onSaveChanges}>
                  {t(strCode.SAVE_BTN)}
                </SmallButton>
              </WrapperContainer>
            ) : (
              <SectionControlButtons
                isPrevDisabled={activeStep === 1}
                isFinish={activeStep === 2}
                onSetActiveStep={onChangeActiveStep}
                onFinish={onFinish}
                loading={loading}
              />
            )
          }>
          {activeStepConfig?.Component || null}
        </SplittedModalContentTemplate>
      </Modal>

      <DiscardModal
        title={
          isEdit
            ? strCode.DISCARD_CAMPAIGN_EDITING_MODAL_TITLE
            : strCode.DISCARD_NOTIFICATION_CAMPAIGN_MODAL_TITLE
        }
        text={
          isEdit
            ? strCode.DISCARD_NOTIFICATION_CAMPAIGN_MODAL_EDIT_TEXT
            : strCode.DISCARD_NOTIFICATION_CAMPAIGN_MODAL_TEXT
        }
        active={discardModalActive}
        setActive={setDiscardModalActive}
        onDiscard={onDiscard}
      />

      <DeactivateCampaignModal
        campaignName={notificationCampaignData?.campaignName}
        active={deactivateModalActive}
        setActive={setDeactivateModalActive}
        onDeactivate={onDeactivateCampaign}
      />
    </>
  );
};

NewNotificationCampaignModal.propTypes = {
  notificationCampaignData: PropTypes.shape({
    campaignName: PropTypes.string.isRequired,
    markets: PropTypes.arrayOf(PropTypes.string).isRequired,
    startTs: PropTypes.string.isRequired,
    endTs: PropTypes.string,
    status: PropTypes.string.isRequired,
    audience: PropTypes.shape({
      deviceTypes: PropTypes.arrayOf(PropTypes.string),
      appTypes: PropTypes.arrayOf(PropTypes.string),
      firmwareVersions: PropTypes.arrayOf(PropTypes.string)
    })
  }),
  active: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired
};

export default NewNotificationCampaignModal;
